import type { CloneVoucherPreviewQuery } from '~/services/PromoService';

/**
 * Primitive tuple for preview cache keys and param equality — avoids relying on
 * object serialization / key order for React Query identity.
 */
export function getCloneVoucherPreviewQueryParts(
  params: CloneVoucherPreviewQuery,
): readonly [string, string | null, string | null, string | null, number | null, boolean] {
  const region = params.region?.trim() ?? '';
  const hasRegion = region.length > 0;
  const amount = params.amount;
  /** Same query object can omit `amount` for region-only clone vs full direct clone — both have no amount. */
  const regionOnlyNoAmount = hasRegion && amount === undefined;
  return [
    params.newVoucherName,
    params.sourceVoucherName ?? null,
    params.sourceVoucherId ?? null,
    params.region ?? null,
    params.amount ?? null,
    regionOnlyNoAmount,
  ] as const;
}

export function areCloneVoucherPreviewParamsEqual(
  a: CloneVoucherPreviewQuery | null,
  b: CloneVoucherPreviewQuery | null,
): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  const pa = getCloneVoucherPreviewQueryParts(a);
  const pb = getCloneVoucherPreviewQueryParts(b);
  return pa[0] === pb[0] && pa[1] === pb[1] && pa[2] === pb[2] && pa[3] === pb[3] && pa[4] === pb[4] && pa[5] === pb[5];
}

export const voucherCloneQueryKeys = {
  all: ['voucher-clone'] as const,
  previews: () => [...voucherCloneQueryKeys.all, 'preview'] as const,
  preview: (params: CloneVoucherPreviewQuery) =>
    [...voucherCloneQueryKeys.previews(), ...getCloneVoucherPreviewQueryParts(params)] as const,
};
